/**
 * CourtSync subscription tiers, marketing feature copy, and tier → feature gating.
 * Single source of truth for Stripe metadata, landing pricing, and in-app plan cards.
 */

export type SubscriptionTier = 'starter' | 'professional' | 'enterprise';

/** One display line; omit comingSoon items when rendering or serializing to Stripe */
export type PlanFeatureItem =
  | string
  | { readonly label: string; readonly comingSoon?: boolean };

function isComingSoonItem(item: PlanFeatureItem): boolean {
  return typeof item !== 'string' && item.comingSoon === true;
}

export function planFeatureLabel(item: PlanFeatureItem): string {
  return typeof item === 'string' ? item : item.label;
}

/** Flat strings for Stripe metadata and APIs; excludes coming-soon lines */
export function flattenPlanFeaturesForStripe(items: readonly PlanFeatureItem[]): string[] {
  return items.filter((i) => !isComingSoonItem(i)).map(planFeatureLabel);
}

const STARTER_FULL: readonly PlanFeatureItem[] = [
  'Public academy page with lesson catalogue',
  'Public request booking for group lessons and camps (no account required)',
  'Owner-managed student enrollment and roster',
  'Owner-managed coach roster for scheduling (no coach logins)',
  'Student and guardian contact records (no member logins)',
  'Terms, lessons, and recurring schedules',
  'Court and coach assignment',
  'Scheduling conflict resolution',
  'Basic attendance visibility',
  'Court assignment and availability',
  'Email and in-app notifications (owner)',
  'Manual payment tracking and billing report',
  'Basic reporting on bookings, students, and revenue',
  'Email support (5 business day response)',
  'Up to 100 students',
] as const;

const PROFESSIONAL_FULL: readonly PlanFeatureItem[] = [
  'All Starter features, plus:',
  'Branded student and guardian member accounts and dashboard',
  'Branded coach login accounts and dashboard',
  'Student and guardian self-service registration and booking',
  'Online checkout with instant booking confirmation',
  'Enrollment from student academy dashboard',
  'Waitlists and make-up credit redemption',
  'Promo codes and coupon support',
  'Sibling and multi-lesson discounts',
  'Rebooking and refund handling',
  'Lesson cancellations and postponements',
  'Make-up lesson workflows',
  'Low enrollment management',
  'Branded student and guardian dashboard',
  'Attendance history and progress tracking',
  'Coach notes and student notifications',
  'Branded coach dashboard',
  'Coach availability and roster',
  'Attendance marking and coach reporting',
  'Development programs and invitation-only pathways',
  'Multiple physical locations',
  'Custom academy branding',
  'Automated reminder workflows',
  'SMS notifications (300/month included)',
  'Automated payment processing with Stripe',
  'Match play, social play, and tournaments',
  'Online checkout with instant booking confirmation',
  'Refund handling',
  'Surcharge pass-through fee controls',
  'Academy coupons and promotions',
  'Trend, attendance, and performance reporting',
  'Up to 2 administrators',
  'Priority support with onboarding assistance',
  'Unlimited students',
] as const;

const ENTERPRISE_FULL: readonly PlanFeatureItem[] = [
  'All Professional features, plus:',
  'SMS notifications (1,000/month included)',
  'Accounting integrations included (Xero, QuickBooks)',
  'Custom integrations and API access',
  'Priority support (24-hour response, emergency support)',
  'Dedicated account manager',
  'Early access to new features',
  'Unlimited administrators',
] as const;

/** Full feature lines per tier (landing, comparison table, Stripe) */
export const PLAN_FEATURES_FULL: Record<SubscriptionTier, readonly PlanFeatureItem[]> = {
  starter: STARTER_FULL,
  professional: PROFESSIONAL_FULL,
  enterprise: ENTERPRISE_FULL,
};

const STARTER_COMPACT: readonly PlanFeatureItem[] = [
  'Public lesson catalogue with request booking',
  'Owner-managed enrollment and roster (no member logins)',
  'Term and lesson scheduling',
  'Email notifications',
  'Manual payment tracking',
  'Basic reporting and analytics',
  'Up to 100 students',
] as const;

/** Compact plan card line for Stripe Connect lesson checkout. */
export const AUTOMATED_STRIPE_PAYMENTS_COMPACT_LABEL = 'Automated payments with Stripe';

const PROFESSIONAL_COMPACT: readonly PlanFeatureItem[] = [
  'Everything in Starter, plus:',
  'Student, guardian, and coach member accounts',
  AUTOMATED_STRIPE_PAYMENTS_COMPACT_LABEL,
  'Waitlists, make-up credits, and coupons',
  'Lesson cancellations and postponements',
  'Branded student and coach dashboards',
  'Development programs and invitation-only pathways',
  'Match play, social play, and tournaments',
  'Multiple physical locations',
  'SMS notifications (300/month included)',
  'Advanced reporting and analytics',
  'Up to 2 administrators',
  'Unlimited students',
] as const;

const ENTERPRISE_COMPACT: readonly PlanFeatureItem[] = [
  'Everything in Professional, plus:',
  'SMS notifications (1,000/month included)',
  'Accounting integrations (Xero, QuickBooks)',
  'Dedicated account manager',
  'Priority 24-hour support',
] as const;

/** Short highlights for registration, settings, in-app plan cards */
export const PLAN_FEATURES_COMPACT: Record<SubscriptionTier, readonly PlanFeatureItem[]> = {
  starter: STARTER_COMPACT,
  professional: PROFESSIONAL_COMPACT,
  enterprise: ENTERPRISE_COMPACT,
};

export const PLAN_MARKETING_COPY: Record<
  SubscriptionTier,
  { readonly name: string; readonly description: string; readonly popular?: boolean }
> = {
  starter: {
    name: 'Starter',
    description:
      'For owner-operated academies with manual enrollment, public request booking, and no coach or member logins',
  },
  professional: {
    name: 'Professional',
    description: 'For growing academies that need automation',
    popular: true,
  },
  enterprise: {
    name: 'Enterprise',
    description: 'For large academies and multi-location operators',
  },
};

/** Stripe / backend: JSON-serializable feature strings */
export function getStripeFeatureStringsForTier(tier: SubscriptionTier): string[] {
  return flattenPlanFeaturesForStripe(PLAN_FEATURES_FULL[tier]);
}

/** Compact strings for UI when API features are empty */
export function getCompactFeatureStringsForTier(tier: SubscriptionTier): string[] {
  return flattenPlanFeaturesForStripe(PLAN_FEATURES_COMPACT[tier]);
}

/**
 * Lines omitted from landing pricing Starter card only (still in full comparison table / PLAN_FEATURES_FULL).
 */
const LANDING_STARTER_CARD_EXCLUDED = new Set<string>([
  'Scheduling conflict resolution',
  'Owner-managed coach roster for scheduling (no coach logins)',
  'Student and guardian contact records (no member logins)',
  'Manual payment tracking and billing report',
  'Email support (5 business day response)',
]);

/** Extra bullets for landing Professional card only (after compact highlights; full table unchanged). */
const PROFESSIONAL_LANDING_EXTRA: readonly PlanFeatureItem[] = [
  'Online checkout with instant booking confirmation',
  'Enrollment from student academy dashboard',
  'Custom academy branding',
  'Sibling and multi-lesson discounts',
  'Automated reminder workflows',
  'Low enrollment management',
] as const;

/**
 * Landing pricing hero cards: Starter = full list minus a few lines (see implementation);
 * Professional = compact highlights plus landing-only extras; Enterprise = compact highlights.
 * Full lists stay in the comparison table.
 */
export function getLandingPricingCardFeatureStrings(tier: SubscriptionTier): string[] {
  if (tier === 'starter') {
    return getStripeFeatureStringsForTier('starter').filter(
      (line) => !LANDING_STARTER_CARD_EXCLUDED.has(line)
    );
  }
  if (tier === 'professional') {
    return [
      ...getCompactFeatureStringsForTier('professional'),
      ...flattenPlanFeaturesForStripe(PROFESSIONAL_LANDING_EXTRA),
    ];
  }
  return getCompactFeatureStringsForTier(tier);
}

export const TierFeatureKey = {
  PAYMENT_PROCESSING: 'payment_processing',
  WAITLISTS: 'waitlists',
  MAKEUP_CREDITS: 'makeup_credits',
  COUPONS_PROMOS: 'coupons_promos',
  DISCOUNTS: 'discounts',
  REBOOKING_REFUNDS: 'rebooking_refunds',
  LESSON_CANCELLATION: 'lesson_cancellation',
  MAKEUP_WORKFLOWS: 'makeup_workflows',
  LOW_ENROLLMENT_MGMT: 'low_enrollment_mgmt',
  BRANDED_DASHBOARDS: 'branded_dashboards',
  ATTENDANCE_HISTORY: 'attendance_history',
  PROGRESS_TRACKING: 'progress_tracking',
  COACH_NOTES: 'coach_notes',
  COACH_AVAILABILITY: 'coach_availability',
  COACH_REPORTING: 'coach_reporting',
  /** Email invites to onboard coaches (distinct from branded dashboards / coach app shell). */
  COACH_INVITES: 'coach_invites',
  CUSTOM_BRANDING: 'custom_branding',
  AUTOMATED_REMINDERS: 'automated_reminders',
  ADVANCED_ANALYTICS: 'advanced_analytics',
  CUSTOM_INTEGRATIONS: 'custom_integrations',
  /** Courts grouped by venue; lessons tied to a location (Professional+) */
  MULTI_LOCATION: 'multi_location',
  /** Structured development programs and skill progress tracking (Professional+) */
  PROGRAMS: 'programs',
  /** Direct Xero / QuickBooks Online OAuth sync (Enterprise) */
  ACCOUNTING_INTEGRATIONS: 'accounting_integrations',
  /** Invite academy admins to help manage the academy (Professional: up to 2; Enterprise: unlimited) */
  ACADEMY_ADMINS: 'academy_admins',
  /** Student-initiated private lessons from the public academy page (Professional+) */
  PRIVATE_LESSON_BOOKING_PUBLIC: 'private_lesson_booking_public',
  /** Charge group term enrolments proportionally for remaining sessions (Professional+) */
  PRO_RATA_TERM_PRICING: 'pro_rata_term_pricing',
  /** Branded public subdomain ({subdomain}.courtsync.io) (Professional+) */
  BRANDED_SUBDOMAIN: 'branded_subdomain',
  /** Guardian transactional SMS (Professional+; monthly allowance per tier) */
  SMS_NOTIFICATIONS: 'sms_notifications',
  /** Branded student/guardian dashboard and academy-scoped member login (Professional+) */
  STUDENT_GUARDIAN_PORTAL: 'student_guardian_portal',
  /** Student/guardian self-service registration and booking (Professional+) */
  STUDENT_SELF_SERVICE_BOOKING: 'student_self_service_booking',
  /** Coach dashboard login and coach-facing tools (Professional+) */
  COACH_PORTAL: 'coach_portal',
  /** Public browse/contact only on Starter; full public booking flow (Professional+) */
  PUBLIC_SELF_SERVICE_BOOKING: 'public_self_service_booking',
  /** Club module: public court hire and guest checkout (requires club_management add-on) */
  COURT_HIRE: 'court_hire',
  /** Club module: membership plans and member subscriptions (requires club_management add-on) */
  MEMBERSHIPS: 'memberships',
  /** Club module: inventory / equipment hire (requires club_management add-on) */
  INVENTORY_HIRE: 'inventory_hire',
  /** Match play, social play, and tournaments (club module or academy Professional+) */
  MATCH_PLAY: 'match_play',
} as const;

export type TierFeatureKey = (typeof TierFeatureKey)[keyof typeof TierFeatureKey];

export const TIER_FEATURE_ACCESS: Record<TierFeatureKey, SubscriptionTier> = {
  [TierFeatureKey.PAYMENT_PROCESSING]: 'professional',
  [TierFeatureKey.WAITLISTS]: 'professional',
  [TierFeatureKey.MAKEUP_CREDITS]: 'professional',
  [TierFeatureKey.COUPONS_PROMOS]: 'professional',
  [TierFeatureKey.DISCOUNTS]: 'professional',
  [TierFeatureKey.REBOOKING_REFUNDS]: 'professional',
  [TierFeatureKey.LESSON_CANCELLATION]: 'professional',
  [TierFeatureKey.MAKEUP_WORKFLOWS]: 'professional',
  [TierFeatureKey.LOW_ENROLLMENT_MGMT]: 'professional',
  [TierFeatureKey.BRANDED_DASHBOARDS]: 'professional',
  [TierFeatureKey.ATTENDANCE_HISTORY]: 'professional',
  [TierFeatureKey.PROGRESS_TRACKING]: 'professional',
  [TierFeatureKey.COACH_NOTES]: 'professional',
  [TierFeatureKey.COACH_AVAILABILITY]: 'professional',
  [TierFeatureKey.COACH_REPORTING]: 'professional',
  [TierFeatureKey.COACH_INVITES]: 'professional',
  [TierFeatureKey.CUSTOM_BRANDING]: 'professional',
  [TierFeatureKey.AUTOMATED_REMINDERS]: 'professional',
  [TierFeatureKey.ADVANCED_ANALYTICS]: 'professional',
  [TierFeatureKey.MULTI_LOCATION]: 'professional',
  [TierFeatureKey.PROGRAMS]: 'professional',
  [TierFeatureKey.ACADEMY_ADMINS]: 'professional',
  [TierFeatureKey.PRIVATE_LESSON_BOOKING_PUBLIC]: 'professional',
  [TierFeatureKey.PRO_RATA_TERM_PRICING]: 'professional',
  [TierFeatureKey.BRANDED_SUBDOMAIN]: 'professional',
  [TierFeatureKey.SMS_NOTIFICATIONS]: 'professional',
  [TierFeatureKey.STUDENT_GUARDIAN_PORTAL]: 'professional',
  [TierFeatureKey.STUDENT_SELF_SERVICE_BOOKING]: 'professional',
  [TierFeatureKey.COACH_PORTAL]: 'professional',
  [TierFeatureKey.PUBLIC_SELF_SERVICE_BOOKING]: 'professional',
  [TierFeatureKey.COURT_HIRE]: 'professional',
  [TierFeatureKey.MEMBERSHIPS]: 'professional',
  [TierFeatureKey.INVENTORY_HIRE]: 'professional',
  [TierFeatureKey.MATCH_PLAY]: 'professional',
  [TierFeatureKey.CUSTOM_INTEGRATIONS]: 'enterprise',
  [TierFeatureKey.ACCOUNTING_INTEGRATIONS]: 'enterprise',
};

/** Feature keys gated by the club_management subscription module (use hasEntitlement, not canAccessFeature). */
export const CLUB_MODULE_FEATURE_KEYS = [
  TierFeatureKey.COURT_HIRE,
  TierFeatureKey.MEMBERSHIPS,
  TierFeatureKey.INVENTORY_HIRE,
] as const satisfies readonly TierFeatureKey[];

/** Max academy_admin role rows per academy by tier (-1 = unlimited). Starter has no additional admins. */
export const ACADEMY_ADMIN_LIMITS: Record<SubscriptionTier, number> = {
  starter: 0,
  professional: 2,
  enterprise: -1,
};

const TIER_RANK: Record<SubscriptionTier, number> = {
  starter: 0,
  professional: 1,
  enterprise: 2,
};

export function getRequiredTier(feature: TierFeatureKey): SubscriptionTier {
  return TIER_FEATURE_ACCESS[feature];
}

export function canAccessFeature(currentTier: SubscriptionTier, feature: TierFeatureKey): boolean {
  const required = TIER_FEATURE_ACCESS[feature];
  return TIER_RANK[currentTier] >= TIER_RANK[required];
}

/** Higher of two tiers (e.g. merge academy row + owner Stripe plan). */
export function maxSubscriptionTier(a: SubscriptionTier, b: SubscriptionTier): SubscriptionTier {
  return TIER_RANK[a] >= TIER_RANK[b] ? a : b;
}

/** Parse tier from Stripe internalId or academy.subscription_tier */
export function normalizeSubscriptionTier(
  value: string | null | undefined
): SubscriptionTier | null {
  if (!value) return null;
  const v = value.toLowerCase();
  if (v === 'starter') return 'starter';
  if (v === 'professional') return 'professional';
  if (v === 'enterprise') return 'enterprise';
  return null;
}

/** Club product line tiers (Club → starter, Club Pro → professional). */
export type ClubPlanTier = 'starter' | 'professional';

const CLUB_STARTER_COMPACT: readonly PlanFeatureItem[] = [
  'Court hire and guest checkout',
  'Membership plans and member portal',
  'Match play, social play, and tournaments',
  'Inventory hire',
  'Facility calendar and closures',
  'Multiple locations',
  'Online payments with Stripe Connect',
  'Public club page',
  'Club operator dashboard',
] as const;

const CLUB_PRO_COMPACT: readonly PlanFeatureItem[] = [
  'Everything in Club, plus:',
  'Branded club subdomain (yourclub.courtsync.io)',
] as const;

/** Short highlights for club plan cards (registration, settings, checkout). */
export const CLUB_PLAN_FEATURES_COMPACT: Record<ClubPlanTier, readonly PlanFeatureItem[]> = {
  starter: CLUB_STARTER_COMPACT,
  professional: CLUB_PRO_COMPACT,
};

export const CLUB_PLAN_MARKETING_COPY: Record<
  ClubPlanTier,
  { readonly name: string; readonly description: string; readonly popular?: boolean }
> = {
  starter: {
    name: 'Club',
    description:
      'Court hire, memberships, match play, and facility operations for racquet sports clubs',
  },
  professional: {
    name: 'Club Pro',
    description: 'Everything in Club plus a branded club subdomain',
    popular: true,
  },
};

/** Compact strings for club plan UI and API hydration (single source of truth). */
export function getCompactFeatureStringsForClubTier(tier: ClubPlanTier): string[] {
  return flattenPlanFeaturesForStripe(CLUB_PLAN_FEATURES_COMPACT[tier]);
}

/** Landing pricing club cards — same compact highlights as onboarding. */
export function getLandingClubPricingCardFeatureStrings(tier: ClubPlanTier): string[] {
  return getCompactFeatureStringsForClubTier(tier);
}

/** Minimum academy tier available as a combined academy + club bundle. */
export const MIN_ACADEMY_BUNDLE_TIER: SubscriptionTier = 'professional';

/** Academy tiers shown as combined bundles on pricing / onboarding. */
export const ACADEMY_BUNDLE_TIERS: readonly SubscriptionTier[] = [
  'professional',
  'enterprise',
] as const;

/** Map any academy tier to a tier that can be sold as a combined bundle. */
export function resolveAcademyBundleTier(tier: SubscriptionTier): SubscriptionTier {
  if ((ACADEMY_BUNDLE_TIERS as readonly string[]).includes(tier)) {
    return tier;
  }
  return MIN_ACADEMY_BUNDLE_TIER;
}

export function isAcademyBundleTier(tier: string | null | undefined): tier is SubscriptionTier {
  return tier != null && (ACADEMY_BUNDLE_TIERS as readonly string[]).includes(tier);
}

/** Default club line tier bundled with each academy tier on combined org checkout. */
export const CLUB_TIER_FOR_ACADEMY_BUNDLE: Record<SubscriptionTier, ClubPlanTier> = {
  starter: 'starter',
  professional: 'professional',
  enterprise: 'professional',
};

/** Map Stripe club product internalId to club_plan_tier. Never use for subscription_tier. */
export function normalizeClubPlanTier(
  internalId: string | null | undefined
): ClubPlanTier | null {
  if (!internalId) return null;
  const v = internalId.toLowerCase();
  if (v === 'club') return 'starter';
  if (v === 'club_pro') return 'professional';
  return null;
}

/** Stripe product line for platform subscription products. */
export type SubscriptionProductLine = 'academy' | 'club';

export function resolveSubscriptionProductLine(
  metadata: Record<string, string> | null | undefined
): SubscriptionProductLine {
  const line = metadata?.productLine?.toLowerCase();
  return line === 'club' ? 'club' : 'academy';
}

/** Infer tier from Stripe product display name when internalId is missing */
export function resolveSubscriptionTierFromPlanName(name: string): SubscriptionTier {
  const n = name.toLowerCase();
  if (n.includes('enterprise')) return 'enterprise';
  if (n.includes('professional')) return 'professional';
  if (n.includes('starter')) return 'starter';
  return 'starter';
}

/**
 * Resolve tier for UI: prefer Stripe metadata internalId, then plan name keywords.
 */
export function resolveSubscriptionTierForDisplay(
  planName: string,
  internalId?: string | null
): SubscriptionTier {
  return normalizeSubscriptionTier(internalId ?? null) ?? resolveSubscriptionTierFromPlanName(planName);
}

/**
 * Short bullet list for narrow plan cards (checkout, settings grid, upgrade flows).
 * Does not use API `features` (those are the full marketing list).
 */
export function getCompactPlanCardFeatures(plan: {
  name: string;
  internalId?: string | null;
}): string[] {
  const clubTier = normalizeClubPlanTier(plan.internalId ?? null);
  if (clubTier) {
    return getCompactFeatureStringsForClubTier(clubTier);
  }
  const tier = resolveSubscriptionTierForDisplay(plan.name, plan.internalId);
  return getCompactFeatureStringsForTier(tier);
}
